import { OutOfCreditsCard } from "@features/billing/components/OutOfCreditsCard";
import { Box, Stack } from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import type { Ref } from "react";
import { ChatInput, type ChatInputHandle } from "./ChatInput";
import { ChatScopeChipFromStore } from "./ChatScopeChip";
import { ChatVersionSwitcher } from "./ChatVersionSwitcher";

interface ChatComposerAreaProps {
  inputRef: Ref<ChatInputHandle>;
  onSend: (text: string) => void;
  onStop: () => void;
  isStreaming: boolean;
  isReconnecting: boolean;
  /**
   * Width-1.0 token used by every full-width row inside ChatContainer.
   * Passed in so the composer wrapper stays visually consistent without
   * re-deriving the same constant in two places.
   */
  contentRow: { readonly width: "100%" };
}

export function ChatComposerArea({
  inputRef,
  onSend,
  onStop,
  isStreaming,
  isReconnecting,
  contentRow,
}: ChatComposerAreaProps) {
  // Sticky out-of-credits payload for the most recent rejected turn.
  // Renders as an inline card above the composer until the user tops
  // up + retries (sendMessage clears it) or explicitly dismisses.
  const insufficientCredits = useChatStore((s) => s.insufficientCredits);
  const setInsufficientCredits = useChatStore((s) => s.setInsufficientCredits);

  return (
    <Box sx={{ flexShrink: 0 }}>
      {insufficientCredits && (
        <Box
          sx={{
            ...contentRow,
            px: { xs: 1.5, sm: 3 },
            pt: 1,
            pb: 0.5,
          }}
        >
          <OutOfCreditsCard
            required={insufficientCredits.required}
            available={insufficientCredits.available}
            onDismiss={() => setInsufficientCredits(null)}
          />
        </Box>
      )}
      <Box sx={{ ...contentRow, px: { xs: 1.5, sm: 3 }, pb: 2, pt: 1 }}>
        {/* Scope chip — only renders on multi-board projects (chip
            is null otherwise). Lets the user pick which PCB(s) the
            agent has visibility into for the rest of the chat,
            independent of the navigational sidebar selection. The
            version switcher sits alongside it: re-point the chat at a
            different schematic version (both hide themselves when
            there's no choice to make). */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 0.75 }}
        >
          <ChatScopeChipFromStore />
          <ChatVersionSwitcher />
        </Stack>
        <ChatInput
          ref={inputRef}
          onSend={onSend}
          onStop={onStop}
          isStreaming={isStreaming}
          disabled={isReconnecting}
          placeholder={
            isReconnecting ? "Reconnecting to the active run…" : undefined
          }
        />
      </Box>
    </Box>
  );
}
